/**
 * Jump, air control, gravity, and landing. The jump launches on the press tick;
 * the cut is the one place a velocity is scaled, and it is flagged as an impulse.
 */

import { impactAttitude } from "../attitude";
import { moveBody, moveResult, probeGround } from "../collision";
import type { EventBuffer } from "../events";
import { abs, moveToward, sign } from "../fastmath";
import { linkOnLand } from "../link";
import type { SimState } from "../state";
import type { CompiledTuning } from "../tuning";
import { DT, Ev, type InputFrame, S } from "../types";
import type { World } from "../world";
import { touchdown } from "./landing";
import { setState } from "./transitions";

const GROUND_PROBE = 0.02;

export function doJump(s: SimState, t: CompiledTuning, ev: EventBuffer, source: number): void {
  // Out of a slide (or its coyote time) the jump is lower and keeps the slide's speed.
  s.vy = s.state === S.Slide || s.slideCoyote === 1 ? t.slideJumpVelocity : t.jumpVelocity;
  s.slideCoyote = 0;
  s.tumbling = 0;
  s.grounded = 0;
  s.coyote = 0;
  s.buffer = 0;
  s.jumpCutArmed = 1;
  ev.push(Ev.Jumped, s.tick, s.vy, source);
  setState(s, ev, S.Jump);
}

/** Handle a jump press or buffered press. Returns true if a jump launched. */
export function tryJump(
  s: SimState,
  input: InputFrame,
  t: CompiledTuning,
  ev: EventBuffer,
  jumpPressed: boolean,
): boolean {
  const canJump = s.grounded === 1 || s.coyote > 0;
  const wantJump = jumpPressed || s.buffer > 0;
  if (canJump && wantJump) {
    const source = jumpPressed ? (s.grounded === 1 ? 0 : 1) : 2;
    doJump(s, t, ev, source);
    if (!input.jump) applyJumpCut(s, t, ev);
    return true;
  }
  if (jumpPressed) s.buffer = t.bufferTicks;
  return false;
}

export function applyJumpCut(s: SimState, t: CompiledTuning, ev: EventBuffer): void {
  if (s.jumpCutArmed === 1 && s.vy > 0) {
    s.vy *= t.jumpCut;
    ev.push(Ev.JumpCut, s.tick, s.vy);
  }
  s.jumpCutArmed = 0;
  if (s.state === S.Jump) setState(s, ev, S.Fall);
}

export function tickAirborne(s: SimState, input: InputFrame, t: CompiledTuning, ev: EventBuffer): void {
  const mx = input.moveX;
  const v0 = s.vx;
  let v = v0;
  if (mx !== 0) {
    s.facing = sign(mx);
    const target = mx * t.maxRunSpeed;
    if (sign(v) !== sign(mx) || abs(v) < abs(target)) {
      v = moveToward(v, target, t.airAccel * DT);
    }
  }
  if (abs(v) > t.maxRunSpeed) {
    v = moveToward(v, sign(v) * t.maxRunSpeed, t.airDrag * DT);
  }
  s.ax = (v - v0) / DT;
  s.vx = v;

  // Jump state ends when the rise ends or the button is released (jump cut).
  if (s.state === S.Jump) {
    if (!input.jump) applyJumpCut(s, t, ev);
    else if (s.vy <= 0) setState(s, ev, S.Fall);
  }

  let scale = 1;
  if (s.state === S.Jump) scale = t.gravityRiseScale;
  else if (s.vy <= 0) scale = t.gravityFallScale;
  const g = t.gravity * scale;
  const vy0 = s.vy;
  s.vy = moveToward(s.vy, -t.terminalVelocity, g * DT);
  s.ay = (s.vy - vy0) / DT;
}

export function land(
  s: SimState,
  input: InputFrame,
  t: CompiledTuning,
  ev: EventBuffer,
  impact: number,
  jumpPressed: boolean,
): void {
  s.impactSpeed = impact;
  s.ticksSinceLand = 0;
  s.slideCoyote = 0;
  s.jumpCutArmed = 0;
  const spinBefore = s.phiDot;
  impactAttitude(s, t, impact, sign(s.vx));
  ev.push(Ev.Landed, s.tick, impact, abs(s.vx));
  linkOnLand(s, ev);
  setState(s, ev, input.moveX !== 0 || s.vx !== 0 ? S.Run : S.Idle);
  touchdown(s, t, ev, spinBefore, s.apexY);
  // A buffered press (or one on this very tick) launches on the landing tick.
  if (s.buffer > 0 || jumpPressed) {
    doJump(s, t, ev, 2);
    if (!input.jump) applyJumpCut(s, t, ev);
  }
}

/** Integrate the body through the world and resolve grounding. */
export function moveAndGround(
  world: World,
  s: SimState,
  input: InputFrame,
  t: CompiledTuning,
  ev: EventBuffer,
  jumpPressed: boolean,
  shapeDy = 0,
): void {
  const wasAirborne = s.grounded === 0;
  moveBody(world, s, t, shapeDy);
  s.collided =
    moveResult.landed |
    (moveResult.headHit << 1) |
    (moveResult.wallHit << 2) |
    (moveResult.cornerNudges > 0 ? 8 : 0);
  if (moveResult.landed === 1) {
    s.grounded = 1;
    if (wasAirborne) land(s, input, t, ev, moveResult.impactSpeed, jumpPressed);
  } else if (s.vy <= 0 && probeGround(world, s, t, GROUND_PROBE)) {
    s.grounded = 1;
    if (wasAirborne) land(s, input, t, ev, -s.vy, jumpPressed);
  } else {
    if (s.grounded === 1) {
      // Walked off an edge: coyote time starts, pose goes airborne now.
      s.coyote = t.coyoteTicks;
      s.slideCoyote = s.state === S.Slide ? 1 : 0;
      setState(s, ev, S.Fall);
    }
    s.grounded = 0;
  }
  if (s.grounded === 1) s.coyote = t.coyoteTicks;
}
